/**
 * Utilidades para lazy loading de librerías pesadas
 * Carga dinámicamente PDF.js, XLSX y Mammoth solo cuando se necesitan
 */

/**
 * Carga PDF.js dinámicamente
 * @returns {Promise<Object>} pdfjsLib cuando está disponible
 */
export async function loadPDFjs() {
  // Si ya está cargado, retornar inmediatamente
  if (typeof window.pdfjsLib !== 'undefined') {
    return window.pdfjsLib;
  }

  // Si el script ya está en proceso de carga, esperar
  if (window._pdfjsLoading) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (typeof window.pdfjsLib !== 'undefined') {
          clearInterval(checkInterval);
          resolve(window.pdfjsLib);
        }
      }, 50);
    });
  }

  // Cargar el script dinámicamente
  window._pdfjsLoading = true;
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/build/pdf.min.js';
    script.async = true;
    
    script.onload = () => {
      // Inicializar el worker
      if (typeof window.pdfjsLib !== 'undefined') {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
          'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/build/pdf.worker.min.js';
        window._pdfjsLoading = false;
        resolve(window.pdfjsLib);
      } else {
        window._pdfjsLoading = false;
        reject(new Error('PDF.js failed to load'));
      }
    };
    
    script.onerror = () => {
      window._pdfjsLoading = false;
      reject(new Error('Failed to load PDF.js script'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * Carga XLSX dinámicamente
 * @returns {Promise<Object>} XLSX cuando está disponible
 */
export async function loadXLSX() {
  // Si ya está cargado, retornar inmediatamente
  if (typeof window.XLSX !== 'undefined') {
    return window.XLSX;
  }

  // Si el script ya está en proceso de carga, esperar
  if (window._xlsxLoading) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (typeof window.XLSX !== 'undefined') {
          clearInterval(checkInterval);
          resolve(window.XLSX);
        }
      }, 50);
    });
  }

  // Cargar el script dinámicamente
  window._xlsxLoading = true;
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
    script.async = true;
    
    script.onload = () => {
      if (typeof window.XLSX !== 'undefined') {
        window._xlsxLoading = false;
        resolve(window.XLSX);
      } else {
        window._xlsxLoading = false;
        reject(new Error('XLSX failed to load'));
      }
    };
    
    script.onerror = () => {
      window._xlsxLoading = false;
      reject(new Error('Failed to load XLSX script'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * Carga Mammoth dinámicamente
 * @returns {Promise<Object>} mammoth cuando está disponible
 */
export async function loadMammoth() {
  // Si ya está cargado, retornar inmediatamente
  if (typeof window.mammoth !== 'undefined') {
    return window.mammoth;
  }

  // Si el script ya está en proceso de carga, esperar
  if (window._mammothLoading) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (typeof window.mammoth !== 'undefined') {
          clearInterval(checkInterval);
          resolve(window.mammoth);
        }
      }, 50);
    });
  }

  // Cargar el script dinámicamente
  window._mammothLoading = true;
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/mammoth/mammoth.browser.min.js';
    script.async = true;
    
    script.onload = () => {
      if (typeof window.mammoth !== 'undefined') {
        window._mammothLoading = false;
        resolve(window.mammoth);
      } else {
        window._mammothLoading = false;
        reject(new Error('Mammoth failed to load'));
      }
    };
    
    script.onerror = () => {
      window._mammothLoading = false;
      reject(new Error('Failed to load Mammoth script'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * Helper para usar PDF.js de forma segura
 * @param {Function} callback - Función que recibe pdfjsLib como parámetro
 * @returns {Promise<any>} Resultado del callback
 */
export async function withPDFjs(callback) {
  const pdfjsLib = await loadPDFjs();
  return callback(pdfjsLib);
}

/**
 * Helper para usar XLSX de forma segura
 * @param {Function} callback - Función que recibe XLSX como parámetro
 * @returns {Promise<any>} Resultado del callback
 */
export async function withXLSX(callback) {
  const XLSX = await loadXLSX();
  return callback(XLSX);
}

/**
 * Helper para usar Mammoth de forma segura
 * @param {Function} callback - Función que recibe mammoth como parámetro
 * @returns {Promise<any>} Resultado del callback
 */
export async function withMammoth(callback) {
  const mammoth = await loadMammoth();
  return callback(mammoth);
}

